/**
 * Layout U — the Section Lab.
 *
 * Every distinct section from R (flagship), S (plugin) and T (R + demo video), plus a new
 * "editor left, controls right" block, rendered as an ordered, toggleable list. Drag the
 * order around in the panel on the right and the page rebuilds instantly.
 *
 * Why this exists: choosing a landing page by asking "which letter?" means rebuilding and
 * re-reading a whole page for every idea. Here the page IS the experiment — reorder, hide
 * what's not earning its place, and the winning order is just a list you can read off.
 *
 * Order and visibility persist to localStorage, so a layout you like survives a refresh and
 * is still there when you show someone.
 *
 * The heavy pieces are the SAME components R and S use — not copies — so anything fixed in
 * the real pages is fixed here too.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../../constants";
import logoDark from "../../../../assets/brand/logo_site.svg";
import logoLight from "../../../../assets/brand/logo_site_onlight.svg";
import FlagshipLiveEt from "./FlagshipLiveEt";
import { FlagshipEtTimeline } from "./FlagshipEtTimeline";
import FlagshipFormats from "./FlagshipFormats";
import { SelectClipsLoop, BlackCherryLoop } from "./FlagshipTwoWaysDemos";
import FlagshipPhoneToTimeline from "./FlagshipPhoneToTimeline";
import FlagshipTikTok from "./FlagshipTikTok";
import UploadDownloadArea from "./UploadDownloadArea";
import { DEMO_YOUTUBE_ID, HEADLINES } from "./FlagshipPage";
import HomeEtEmbed from "./HomeEtEmbed";
import { LAB_SECTIONS, LAB_ORDER_KEY, LAB_HIDDEN_KEY, LAB_FONTS, LAB_FONT_KEY, LAB_CUSTOM_KEY, LabSectionId } from "../constants/labSections";

const THEME_KEY = "cc_flagship_theme";
type Theme = "light" | "dark";

const SectionLabPage: React.FC = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState<Theme>("dark");
  const [order, setOrder] = useState<LabSectionId[]>(LAB_SECTIONS.map((s) => s.id));
  const [hidden, setHidden] = useState<LabSectionId[]>([]);
  const [panelOpen, setPanelOpen] = useState(true);
  // Which of R's rotating headlines the hero is showing. Click one in "All hero texts" to
  // pin it here — that's how you audition a headline instead of waiting for the carousel.
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroAuto, setHeroAuto] = useState(true);
  // Type your own headline and see it in place — so trying a word doesn't require asking
  // anyone. Empty means "use R's rotation".
  const [custom, setCustom] = useState("");
  const [fontId, setFontId] = useState(LAB_FONTS[0].id);
  const fontStack = (LAB_FONTS.find((f) => f.id === fontId) || LAB_FONTS[0]).stack;

  useEffect(() => {
    if (!heroAuto) return undefined;
    const t = window.setInterval(() => setHeroIdx((i) => (i + 1) % HEADLINES.length), 4200);
    return () => window.clearInterval(t);
  }, [heroAuto]);
  const logo = theme === "light" ? logoLight : logoDark;

  useEffect(() => {
    try {
      const t = localStorage.getItem(THEME_KEY);
      if (t === "light" || t === "dark") setTheme(t);
      const savedOrder = JSON.parse(localStorage.getItem(LAB_ORDER_KEY) || "null");
      if (Array.isArray(savedOrder)) {
        // Keep any section added since the order was saved, instead of silently dropping it.
        const known = LAB_SECTIONS.map((s) => s.id);
        const kept = savedOrder.filter((id: LabSectionId) => known.includes(id));
        setOrder([...kept, ...known.filter((id) => !kept.includes(id))]);
      }
      const savedHidden = JSON.parse(localStorage.getItem(LAB_HIDDEN_KEY) || "null");
      if (Array.isArray(savedHidden)) setHidden(savedHidden);
      const savedFont = localStorage.getItem(LAB_FONT_KEY);
      if (savedFont && LAB_FONTS.some((f) => f.id === savedFont)) setFontId(savedFont);
      const savedCustom = localStorage.getItem(LAB_CUSTOM_KEY);
      if (savedCustom) { setCustom(savedCustom); setHeroAuto(false); }
    } catch (e) {
      /* ignore */
    }
  }, []);

  const persist = useCallback((nextOrder: LabSectionId[], nextHidden: LabSectionId[]) => {
    try {
      localStorage.setItem(LAB_ORDER_KEY, JSON.stringify(nextOrder));
      localStorage.setItem(LAB_HIDDEN_KEY, JSON.stringify(nextHidden));
    } catch (e) {
      /* ignore */
    }
  }, []);

  const move = (id: LabSectionId, dir: -1 | 1) => {
    const i = order.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
    persist(next, hidden);
  };

  const toggle = (id: LabSectionId) => {
    const next = hidden.includes(id) ? hidden.filter((h) => h !== id) : [...hidden, id];
    setHidden(next);
    persist(order, next);
  };

  const reset = () => {
    const next = LAB_SECTIONS.map((s) => s.id);
    setOrder(next);
    setHidden([]);
    persist(next, []);
  };

  const toggleTheme = () =>
    setTheme((t) => {
      const next: Theme = t === "dark" ? "light" : "dark";
      try { localStorage.setItem(THEME_KEY, next); } catch (e) { /* ignore */ }
      return next;
    });

  /** The actual content of each section, keyed by id. */
  const CONTENT: Record<LabSectionId, React.ReactNode> = {
    hero: (
      <div className="wrap lab-hero">
        <span className="eyebrow">The spreadsheet for video</span>
        <h1 className="lab-rot" style={{ fontFamily: fontStack }}>
          {custom.trim()
            ? custom.split("\n").map((line, i) => <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>)
            : HEADLINES[heroIdx]}
        </h1>
        <p className="sub">
          Upload a video and every sentence — every silence too — becomes its own clip, in a
          sheet you can search, filter and drag straight onto your Premiere timeline.
        </p>
        <div className="lab-cta">
          <button className="btn btn-cherry" onClick={() => navigate(ROUTES.HOME)}>Try it free</button>
          <button className="btn btn-ghost" onClick={() => navigate(ROUTES.PRICING)}>Pricing</button>
        </div>
      </div>
    ),
    // Every headline R rotates through, all visible at once. Click one to pin it in the hero
    // above — auditioning copy is the thing you actually want to do here.
    heroTexts: (
      <div className="wrap">
        <div className="lab-head">
          <span className="eyebrow">Hero copy</span>
          <h2>All the hero texts.</h2>
          <p>Click one to pin it in the hero section. Click again to resume rotating.</p>
        </div>
        <div className="lab-editor">
          <label className="lab-lbl mono">Write your own</label>
          <textarea
            className="lab-input"
            style={{ fontFamily: fontStack }}
            rows={2}
            value={custom}
            placeholder={"Type a headline…\nEnter for a second line"}
            onChange={(e) => {
              setCustom(e.target.value);
              setHeroAuto(!e.target.value.trim());
              try { localStorage.setItem(LAB_CUSTOM_KEY, e.target.value); } catch (err) { /* ignore */ }
            }}
          />
          <label className="lab-lbl mono">Font</label>
          <div className="lab-fonts">
            {LAB_FONTS.map((f) => (
              <button
                key={f.id}
                className={`lab-font${fontId === f.id ? " on" : ""}`}
                style={{ fontFamily: f.stack }}
                onClick={() => {
                  setFontId(f.id);
                  try { localStorage.setItem(LAB_FONT_KEY, f.id); } catch (err) { /* ignore */ }
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          {custom.trim() ? (
            <button
              className="btn-sm"
              onClick={() => {
                setCustom("");
                setHeroAuto(true);
                try { localStorage.removeItem(LAB_CUSTOM_KEY); } catch (err) { /* ignore */ }
              }}
            >
              Clear — back to the rotation
            </button>
          ) : null}
        </div>

        <div className="lab-texts">
          {HEADLINES.map((h, i) => (
            <button
              key={i}
              className={`lab-text${!heroAuto && heroIdx === i ? " on" : ""}`}
              onClick={() => {
                if (!heroAuto && heroIdx === i) { setHeroAuto(true); return; }
                setHeroIdx(i);
                setHeroAuto(false);
              }}
            >
              <span className="n mono">{String(i + 1).padStart(2, "0")}</span>
              <span className="t" style={{ fontFamily: fontStack }}>{h}</span>
            </button>
          ))}
        </div>
      </div>
    ),
    heroA: (
      <div className="wrap lab-hero">
        <img src={logo} alt="" height={52} style={{ width: "auto", margin: "0 auto 14px", display: "block" }} />
        <h1>
          Every moment becomes a clip.
          <br />
          <span className="rw">Cherry-pick the ones you want.</span>
        </h1>
        <p className="sub">
          Upload a video or audio file. We cut every quote and silence into its own clip,
          ready to play, download and share in one click.
        </p>
      </div>
    ),
    whatItDoes: (
      <div className="wrap">
        <div className="lab-head center"><span className="eyebrow">What it does</span><h2>Three things, in order.</h2></div>
        <div className="lab-steps">
          <div className="lab-step"><span className="k mono">01</span><h3>You upload</h3><p>A video, an audio file, or a YouTube link. That's the whole setup.</p></div>
          <div className="lab-step"><span className="k mono">02</span><h3>It cuts everything</h3><p>Every sentence becomes its own clip. Every silence too. Nothing is thrown away.</p></div>
          <div className="lab-step"><span className="k mono">03</span><h3>You cherry-pick</h3><p>Search, filter and tick. Export them, or drop them straight onto a Premiere timeline.</p></div>
        </div>
      </div>
    ),
    bridgeB: (
      <div className="wrap">
        <div className="lab-bridge">
          <div className="br br-pick">
            <img src={logo} alt="" height={34} style={{ width: "auto" }} />
            <div><strong>CherryPick your clips</strong><span>Filter, sort and tick the moments — you're in control.</span></div>
          </div>
          <span className="br-or mono">or</span>
          <div className="br br-ai">
            <div><strong>Chat with Black Cherry</strong><span>Tell Cherry the reel you want — it picks for you.</span></div>
          </div>
        </div>
      </div>
    ),
    etEmbedB: (
      <div className="wrap">
        <div className="lab-head"><span className="eyebrow">The editor, inline</span><h2>No click needed — it's just here.</h2></div>
        <HomeEtEmbed />
      </div>
    ),
    liveEt: (
      <div className="wrap">
        <div className="lab-head"><span className="eyebrow">The real editor</span><h2>This is the actual product, running here.</h2></div>
        <FlagshipLiveEt bare controls defaultMode="desktop" />
      </div>
    ),
    video: (
      <div className="wrap">
        <div className="lab-head"><span className="eyebrow">Watch it work</span><h2>Sixty seconds, one interview.</h2></div>
        <div className="lab-video">
          <iframe
            src={`https://www.youtube.com/embed/${DEMO_YOUTUBE_ID}`}
            title="CherryClipz demo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    ),
    // The B-layout idea Isaac asked for: editor on the left, the few controls that matter on
    // the right, so the page explains the interface without a feature grid.
    etControls: (
      <div className="wrap">
        <div className="lab-head"><span className="eyebrow">How you drive it</span><h2>Sheet on the left. Three controls on the right.</h2></div>
        <div className="lab-split">
          <div className="lab-split-et"><FlagshipLiveEt bare defaultMode="desktop" /></div>
          <div className="lab-split-ctl">
            <div className="ctl"><span className="ctl-k mono">Search</span><p>Type a word. The sheet filters to every clip that says it.</p></div>
            <div className="ctl"><span className="ctl-k mono">Tick</span><p>Pick clips in any order. The number is the order they'll cut in.</p></div>
            <div className="ctl"><span className="ctl-k mono">Export</span><p>One clip or a hundred — mp4, mp3, gif, srt, vtt or a zip.</p></div>
          </div>
        </div>
      </div>
    ),
    choose: (
      <div className="wrap">
        <div className="lab-head center"><span className="eyebrow">Two ways to cut</span><h2>Choose<span className="cw-dot">.</span></h2></div>
        <div className="lab-ways">
          <div className="way">
            <span className="way-tag red">Human choice</span>
            <div className="way-card"><SelectClipsLoop /></div>
            <p>Read the sheet and pick the lines you want.</p>
          </div>
          <div className="way">
            <span className="way-tag pl">AI prompt</span>
            <div className="way-card"><BlackCherryLoop /></div>
            <p>Or tell Black Cherry what you're after and let it pick.</p>
          </div>
        </div>
      </div>
    ),
    premiere: (
      <div className="wrap">
        <div className="lab-head"><span className="eyebrow">Premiere Pro plugin</span><h2>Cherry-pick on your phone. It cuts in Premiere.</h2></div>
        <FlagshipPhoneToTimeline />
      </div>
    ),
    timeline: (
      <div className="wrap">
        <div className="lab-head"><span className="eyebrow">Sheet → timeline</span><h2>Ticks become cuts.</h2></div>
        <FlagshipEtTimeline />
      </div>
    ),
    formats: (
      <div className="wrap">
        <div className="lab-head center"><span className="eyebrow">Every screen</span><h2>It reflows to whatever you open it on.</h2></div>
        <FlagshipFormats />
      </div>
    ),
    tiktok: (
      <div className="wrap">
        <div className="lab-head center"><span className="eyebrow">Vertical</span><h2>Cut once, post anywhere.</h2></div>
        <FlagshipTikTok />
      </div>
    ),
    upload: (
      <div className="wrap">
        <div className="lab-head center"><span className="eyebrow">Your turn</span><h2>Now make it <span className="rw">yours</span>.</h2></div>
        <div className="lab-upload"><UploadDownloadArea /></div>
      </div>
    ),
    pricing: (
      <div className="wrap">
        <div className="lab-head center">
          <span className="eyebrow">Pricing</span>
          <h2>Free to start. Pro when you're rolling.</h2>
          <p>Every quote and every silence becomes its own clip. Start free, on the web or in Premiere Pro.</p>
        </div>
        <div className="lab-cta center">
          <button className="btn btn-cherry" onClick={() => navigate(ROUTES.PRICING)}>See plans</button>
        </div>
      </div>
    ),
  };

  const visible = order.filter((id) => !hidden.includes(id));

  return (
    <div className="cclab" data-theme={theme}>
      <style>{LAB_CSS}</style>

      <nav className="lab-nav">
        <button className="brand-btn" onClick={() => navigate(ROUTES.HOME)}>
          <img src={logo} alt="" height={26} style={{ width: "auto" }} />
          <span className="wordmark">Cherry<span className="ck">Clipz</span></span>
        </button>
        <div className="lab-nav-right">
          <button className="linkbtn" onClick={() => navigate(ROUTES.MY_LIBRARY)}>My library</button>
          <button className="linkbtn" onClick={() => navigate(ROUTES.PRICING)}>Pricing</button>
          <button className="linkbtn" onClick={toggleTheme}>{theme === "dark" ? "Light" : "Dark"}</button>
        </div>
      </nav>

      {visible.map((id) => {
        const meta = LAB_SECTIONS.find((s) => s.id === id);
        return (
          <section className="lab-sec" id={`lab-${id}`} key={id} data-sec={meta?.label}>
            {CONTENT[id]}
          </section>
        );
      })}

      {/* The panel. Fixed, collapsible, and deliberately plain — it's a tool, not a design. */}
      <div className={`lab-panel${panelOpen ? "" : " closed"}`}>
        <div className="lab-panel-head">
          <strong>Sections</strong>
          <button className="linkbtn" onClick={() => setPanelOpen((o) => !o)}>
            {panelOpen ? "Hide ›" : "‹"}
          </button>
        </div>
        {panelOpen && (
          <>
            <ol className="lab-list">
              {order.map((id, i) => {
                const meta = LAB_SECTIONS.find((s) => s.id === id);
                const off = hidden.includes(id);
                return (
                  <li key={id} className={off ? "off" : ""}>
                    <span className="n mono">{i + 1}</span>
                    <span className="nm" title={meta?.from}>{meta?.label}</span>
                    <span className="src mono">{meta?.from}</span>
                    <button onClick={() => move(id, -1)} disabled={i === 0} aria-label="Move up">↑</button>
                    <button onClick={() => move(id, 1)} disabled={i === order.length - 1} aria-label="Move down">↓</button>
                    <button onClick={() => toggle(id)} aria-label={off ? "Show" : "Hide"}>{off ? "○" : "●"}</button>
                  </li>
                );
              })}
            </ol>
            <div className="lab-panel-foot">
              <button className="btn-sm" onClick={reset}>Reset</button>
              <span className="mono">{visible.length}/{order.length} shown</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const LAB_CSS = `
.cclab[data-theme="light"]{--bg:#f4f1ee;--surface:#fff;--surface-2:#faf7f4;--line:#e9e3dd;--line-2:#ddd5ce;--ink:#181315;--muted:#75696e;--faint:#a89ea3;--cherry:#e01e49;--shadow:0 30px 60px -34px rgba(60,20,30,.28)}
.cclab[data-theme="dark"]{--bg:#0e0b0d;--surface:#161114;--surface-2:#1b1418;--line:#282025;--line-2:#38292f;--ink:#f5edf0;--muted:#a99ca2;--faint:#7a6d73;--cherry:#ec2b57;--shadow:0 40px 80px -40px rgba(0,0,0,.8)}
.cclab{--sans:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;--mono:ui-monospace,"SF Mono",Menlo,Consolas,monospace;min-height:100vh;background:var(--bg);color:var(--ink);font-family:var(--sans);line-height:1.5;-webkit-font-smoothing:antialiased}
.cclab *{box-sizing:border-box}
.cclab .wrap{max-width:1140px;margin:0 auto;padding:0 28px}
.cclab .mono{font-family:var(--mono)}
.cclab h1,.cclab h2{margin:0;font-weight:770;letter-spacing:-.03em;line-height:1.05;text-wrap:balance}
.cclab .eyebrow{font-family:var(--mono);font-size:12px;letter-spacing:.22em;text-transform:uppercase;color:var(--faint)}
.cclab .rw{color:var(--cherry)}
.cclab .linkbtn{font-family:inherit;background:none;border:none;padding:0;cursor:pointer;color:var(--muted);font-size:14px}
.cclab .linkbtn:hover{color:var(--ink)}
.cclab .btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;font-family:var(--sans);font-weight:650;font-size:15px;border-radius:999px;padding:13px 24px;border:1px solid transparent;cursor:pointer;background:transparent;color:var(--ink);transition:transform .14s ease,filter .2s ease}
.cclab .btn-cherry{background:var(--cherry);color:#fff}
.cclab .btn-cherry:hover{transform:translateY(-1px);filter:brightness(1.06)}
.cclab .btn-ghost{border-color:var(--line-2)}
.cclab .btn-ghost:hover{border-color:var(--cherry)}
.cclab :focus-visible{outline:2px solid var(--cherry);outline-offset:3px;border-radius:8px}

.cclab .lab-nav{position:sticky;top:0;z-index:30;display:flex;align-items:center;gap:20px;height:66px;padding:0 28px;background:color-mix(in srgb,var(--bg) 85%,transparent);backdrop-filter:blur(12px);border-bottom:1px solid var(--line)}
.cclab .brand-btn{background:none;border:none;cursor:pointer;display:flex;align-items:center;gap:9px;padding:0}
.cclab .wordmark{font-weight:820;font-size:19px;letter-spacing:-.02em;color:var(--ink)}
.cclab .wordmark .ck{color:var(--cherry)}
.cclab .lab-nav-right{margin-left:auto;display:flex;gap:20px}

.cclab .lab-sec{padding:74px 0;border-top:1px solid var(--line);position:relative}
.cclab .lab-sec:nth-child(odd){background:var(--surface-2)}
/* A quiet label so you always know which block you're looking at while rearranging. */
.cclab .lab-sec::before{content:attr(data-sec);position:absolute;top:10px;left:14px;font-family:var(--mono);font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--faint);opacity:.55}
.cclab .lab-head{max-width:640px;margin:0 0 30px}
.cclab .lab-head.center{margin-left:auto;margin-right:auto;text-align:center}
.cclab .lab-head h2{font-size:clamp(26px,3.4vw,40px);margin-top:10px}
.cclab .lab-head p{color:var(--muted);font-size:16px;margin-top:12px}

.cclab .lab-hero{text-align:center;padding:26px 28px 0}
.cclab .lab-hero h1{font-size:clamp(38px,5.6vw,68px);margin-top:14px}
.cclab .lab-hero .sub{max-width:560px;margin:18px auto 0;color:var(--muted);font-size:17px}
.cclab .lab-cta{display:flex;gap:12px;margin-top:28px;flex-wrap:wrap;justify-content:center}
.cclab .lab-cta.center{justify-content:center}

/* the rotating headline, matching R's scale */
.cclab .lab-rot{min-height:1.9em}
.cclab .lab-rot .strike-anim,.cclab .lab-rot .hl{position:relative}

/* write-your-own headline + font switcher */
.cclab .lab-editor{background:var(--surface);border:1px solid var(--line-2);border-radius:14px;padding:20px;margin-bottom:22px;display:flex;flex-direction:column;gap:10px;align-items:flex-start}
.cclab .lab-lbl{font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:var(--faint)}
.cclab .lab-input{width:100%;background:var(--bg);color:var(--ink);border:1px solid var(--line-2);border-radius:10px;padding:14px 16px;font-size:24px;font-weight:770;letter-spacing:-.02em;line-height:1.2;resize:vertical}
.cclab .lab-input:focus{outline:2px solid var(--cherry);outline-offset:1px}
.cclab .lab-fonts{display:flex;gap:8px;flex-wrap:wrap}
.cclab .lab-font{background:var(--surface-2);border:1px solid var(--line-2);color:var(--ink);border-radius:999px;padding:7px 15px;font-size:14px;cursor:pointer}
.cclab .lab-font:hover{border-color:var(--cherry)}
.cclab .lab-font.on{background:var(--cherry);border-color:var(--cherry);color:#fff}

/* every hero line at once — the audition board */
.cclab .lab-texts{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:12px}
.cclab .lab-text{display:flex;gap:12px;align-items:flex-start;text-align:left;font-family:inherit;cursor:pointer;background:var(--surface);border:1px solid var(--line-2);border-radius:14px;padding:18px 20px;color:var(--ink);transition:border-color .18s ease,transform .14s ease}
.cclab .lab-text:hover{border-color:var(--cherry);transform:translateY(-2px)}
.cclab .lab-text.on{border-color:var(--cherry);box-shadow:0 0 0 1px var(--cherry)}
.cclab .lab-text .n{color:var(--faint);font-size:11px;padding-top:5px;flex:none}
.cclab .lab-text .t{font-size:21px;font-weight:770;letter-spacing:-.02em;line-height:1.15}
.cclab .lab-text .rw{color:var(--cherry)}

/* three-step explainer */
.cclab .lab-steps{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
.cclab .lab-step{background:var(--surface);border:1px solid var(--line);border-radius:14px;padding:24px}
.cclab .lab-step .k{font-size:12px;color:var(--cherry);letter-spacing:.1em;font-weight:700}
.cclab .lab-step h3{font-size:19px;margin:10px 0 6px;font-weight:750}
.cclab .lab-step p{margin:0;color:var(--muted);font-size:14px}
@media (max-width:760px){.cclab .lab-steps{grid-template-columns:1fr}}

/* CherryPick or Chat bridge (from B) */
.cclab .lab-bridge{display:grid;grid-template-columns:1fr auto 1fr;gap:16px;align-items:stretch}
.cclab .br{display:flex;align-items:center;gap:16px;border-radius:16px;padding:20px 24px}
.cclab .br strong{display:block;font-size:18px;font-weight:800}
.cclab .br span{display:block;font-size:14px;color:var(--muted);margin-top:2px}
.cclab .br-pick{border:1px solid rgba(236,43,87,.55);background:linear-gradient(135deg,rgba(236,43,87,.22),rgba(236,43,87,.04))}
.cclab .br-ai{border:1px solid rgba(124,58,237,.6);background:linear-gradient(135deg,rgba(124,58,237,.22),rgba(124,58,237,.04))}
.cclab .br-or{display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;color:var(--faint);text-transform:uppercase;letter-spacing:.1em}
@media (max-width:820px){.cclab .lab-bridge{grid-template-columns:1fr}}

.cclab .lab-video{position:relative;width:100%;max-width:900px;margin:0 auto;aspect-ratio:16/9;border-radius:16px;overflow:hidden;border:1px solid var(--line);box-shadow:var(--shadow);background:#000}
.cclab .lab-video iframe{position:absolute;inset:0;width:100%;height:100%;border:0}

.cclab .lab-split{display:grid;grid-template-columns:1.5fr .85fr;gap:34px;align-items:center}
.cclab .lab-split-ctl{display:flex;flex-direction:column;gap:22px}
.cclab .ctl-k{display:inline-block;font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:var(--cherry);font-weight:700}
.cclab .ctl p{margin:6px 0 0;color:var(--muted);font-size:15px}
@media (max-width:900px){.cclab .lab-split{grid-template-columns:1fr}}

.cclab .lab-ways{display:grid;grid-template-columns:1fr 1fr;gap:34px;max-width:980px;margin:0 auto}
.cclab .way{display:flex;flex-direction:column;align-items:center;text-align:center}
.cclab .way-tag{font-family:var(--mono);font-size:13px;font-weight:800;letter-spacing:.16em;text-transform:uppercase;color:#fff;padding:7px 22px;border-radius:999px;margin-bottom:14px}
.cclab .way-tag.red{background:var(--cherry)}
.cclab .way-tag.pl{background:#7c3aed}
.cclab .way-card{width:100%;border:1px solid var(--line-2);border-radius:16px;padding:22px;background:var(--surface)}
.cclab .way p{color:var(--muted);font-size:15px;margin:14px 0 0;max-width:320px}
.cclab .cw-dot{color:var(--cherry)}
@media (max-width:760px){.cclab .lab-ways{grid-template-columns:1fr}}

.cclab .lab-upload{max-width:760px;margin:0 auto}

.cclab .lab-panel{position:fixed;right:14px;bottom:14px;z-index:60;width:308px;max-height:72vh;overflow:auto;background:var(--surface);border:1px solid var(--line-2);border-radius:14px;box-shadow:var(--shadow);font-size:13px}
.cclab .lab-panel.closed{width:auto}
.cclab .lab-panel-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:11px 13px;border-bottom:1px solid var(--line);position:sticky;top:0;background:var(--surface)}
.cclab .lab-list{list-style:none;margin:0;padding:6px}
.cclab .lab-list li{display:flex;align-items:center;gap:6px;padding:5px 6px;border-radius:8px}
.cclab .lab-list li:hover{background:var(--surface-2)}
.cclab .lab-list li.off .nm,.cclab .lab-list li.off .src{opacity:.4;text-decoration:line-through}
.cclab .lab-list .n{width:16px;color:var(--faint);font-size:11px}
.cclab .lab-list .nm{flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cclab .lab-list .src{font-size:10px;color:var(--faint);flex:none}
.cclab .lab-list button{background:var(--surface-2);border:1px solid var(--line-2);color:var(--ink);border-radius:6px;width:22px;height:22px;cursor:pointer;font-size:11px;line-height:1;flex:none}
.cclab .lab-list button:disabled{opacity:.3;cursor:default}
.cclab .lab-list button:hover:not(:disabled){border-color:var(--cherry)}
.cclab .lab-panel-foot{display:flex;align-items:center;justify-content:space-between;padding:9px 13px;border-top:1px solid var(--line);color:var(--faint);font-size:11px}
.cclab .btn-sm{background:var(--surface-2);border:1px solid var(--line-2);color:var(--ink);border-radius:8px;padding:5px 12px;cursor:pointer;font-size:12px}
.cclab .btn-sm:hover{border-color:var(--cherry)}
@media (max-width:640px){.cclab .lab-panel{display:none}}
`;

export default SectionLabPage;
